import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/hooks/useAuth';

/**
 * LoginRedirect component handles immediate redirection after successful login
 * This component prevents 404 flash by immediately redirecting to the journey
 */
export default function LoginRedirect() {
  const [, setLocation] = useLocation();
  const { isAuthenticated, user, isLoading } = useAuth();
  const [redirectAttempts, setRedirectAttempts] = useState(0);
  const [hasRedirected, setHasRedirected] = useState(false);

  useEffect(() => {
    console.log('LoginRedirect: Checking auth state', { isAuthenticated, user: !!user, isLoading, redirectAttempts });
    
    // Prevent infinite redirect loops
    if (hasRedirected) {
      console.log('LoginRedirect: Already redirected, skipping');
      return;
    }
    
    // Prevent too many redirect attempts
    if (redirectAttempts >= 3) {
      console.warn('LoginRedirect: Too many redirect attempts, stopping to prevent infinite loop');
      setLocation('/login');
      setHasRedirected(true);
      return;
    }
    
    // Clean up URL params
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('login') === 'success') {
      window.history.replaceState({}, '', '/');
    }
    
    if (!isLoading) {
      if (isAuthenticated && user) {
        console.log('LoginRedirect: User authenticated, redirecting to journey');
        // Set login success flag for ComponentBasedJourney
        sessionStorage.setItem('loginSuccess', 'true');
        setLocation('/');
        setHasRedirected(true);
      } else {
        console.log('LoginRedirect: User not authenticated, redirecting to login');
        setRedirectAttempts(prev => prev + 1);
        
        // Add a small delay to prevent rapid redirects
        setTimeout(() => {
          setLocation('/login');
          setHasRedirected(true);
        }, 500);
      }
    }
  }, [setLocation, isAuthenticated, user, isLoading, redirectAttempts, hasRedirected]);

  // Show loading state during redirect - match the app's loading style to prevent flash
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rh-helicopter-red mx-auto mb-4"></div>
        <p className="text-gray-600">Redirecting to your journey...</p>
      </div>
    </div>
  );
}